import React, { useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toastHelper } from '../../utils/toastHelper';
import { setLocalStorage } from '../../utils/localStorageManager.js';
import { googleOAuthService } from '../../services/googleOAuthService.ts';
import { useAuth } from './AuthContext';
import { useYouTubeData } from '../../contexts/AppDataContext';

// Global flag to prevent duplicate callback processing across component remounts
let isProcessingCallback = false;

const GoogleCallbackHandler = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const hasRunRef = useRef(false);
  const { setIsAuthenticated, isLoading } = useAuth();
  const { fetchYouTubeData } = useYouTubeData();

  useEffect(() => {
    if (isLoading || hasRunRef.current || isProcessingCallback) {
      return;
    }

    const authCode = searchParams.get('code');
    const user_id = searchParams.get('state');
    const user_email = searchParams.get('login_hint')
    const error = searchParams.get('error');

    if (!authCode || !user_id || error) {
      toastHelper.newMessage('error', 'Login Failed', error ? 'Google OAuth failed. Please try again.' : 'Missing OAuth parameters.');
      navigate('/');
      return;
    }

    hasRunRef.current = true;
    isProcessingCallback = true;

    googleOAuthService
      .callback({ code: authCode, user_id, user_email})
      .then((data) => {
        if (data.jwt) {
          setLocalStorage(data.jwt, data.expires_in, data.user_id, data.user_email);
          setIsAuthenticated(true);
          toastHelper.newMessage('success', 'Login Successful', 'You are now authenticated.');
          // Refetch YouTube data after successful OAuth callback
          // Use force=true to bypass 401 flag check
          fetchYouTubeData(true).catch((err) => {
            // Silently fail - user can retry manually if needed
            console.error('Failed to refetch YouTube data after OAuth:', err);
          });
          navigate('/');
        } else {
          throw new Error('Authentication failed: No JWT.');
        }
      })
      .catch((err) => {
        console.error('Google OAuth callback error:', err);
        toastHelper.newMessage('error', 'Login Failed', 'Could not authenticate user. Please try again.');
        navigate('/');
      })
      .finally(() => {
        isProcessingCallback = false;
      });
  }, [searchParams, navigate, setIsAuthenticated, isLoading, fetchYouTubeData]);

  return (
    <div style={{ textAlign: 'center', marginTop: '20%' }}>
      <h2>Processing Google Authentication...</h2>
    </div>
  );
};

export default GoogleCallbackHandler;